// FolderTree — left-pane folder navigation. Virtual folders backed by
// assetRegistry. Right-click for context menu (Rename / Delete / New
// subfolder). Inline rename — F2 or double-click the row. Drag-drop target
// for both OS files and assets dragged from the grid.

import React, { useMemo, useState, useEffect, useRef } from 'react'
import { assetRegistry } from '../../lib/assetRegistry'
import { isHiddenFolderKey, isHiddenFolderSegments, showHiddenFolders, validateFolderName } from '../../lib/hiddenFolders'
import NewAssetMenu from './NewAssetMenu'
import { dialog } from '../../lib/dialog'
import { head, activeWash } from '../../lib/studioStyles.js'

const C = {
  bg:       'var(--m-panel, #0c0e15)',
  hover:    'var(--m-panel-hi, #1a1d28)',
  active:   'var(--m-panel-hi, #1f2a5c)',
  text:     'var(--m-fg, #e1e1ed)',
  dim:      'var(--m-fg-2, #8f8fa2)',
  border:   'var(--m-line, #1f2129)',
  primary:  'var(--m-accent, #bec2ff)',
  menuBg:   'var(--m-panel, #1a1d28)',
  menuHover:'var(--m-panel-hi, #252938)',
}

function buildTree(folderPaths) {
  // folderPaths: string[] of "/"-joined paths including ''. Build a nested
  // { name, path, children } tree rooted at ''.
  const root = { name: '/', path: [], children: [] }
  const lookup = { '': root }
  const sorted = [...folderPaths].sort((a, b) => a.split('/').length - b.split('/').length)
  for (const key of sorted) {
    if (key === '') continue
    const parts = key.split('/')
    const parentKey = parts.slice(0, -1).join('/')
    const parent = lookup[parentKey] ?? root
    const node = { name: parts[parts.length - 1], path: parts, children: [] }
    parent.children.push(node)
    lookup[key] = node
  }
  return root
}

const COLLAPSE_LS_KEY = 'contentBrowser.collapsed'

function loadCollapsed() {
  if (typeof localStorage === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(COLLAPSE_LS_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return new Set(Array.isArray(arr) ? arr : [])
  } catch { return new Set() }
}
function saveCollapsed(set) {
  if (typeof localStorage === 'undefined') return
  try { localStorage.setItem(COLLAPSE_LS_KEY, JSON.stringify(Array.from(set))) } catch { /* ignore */ }
}

export default function FolderTree({ activePath, onSelectPath, onDropAsset, onDropFiles, onRootContextMenu, onCreateAtPath }) {
  const [, force] = useState(0)
  const [menu, setMenu] = useState(null) // { x, y, path } | null  (path=[] for root)
  const [renaming, setRenaming] = useState(null) // joined key of folder being renamed | null
  const [collapsed, setCollapsed] = useState(loadCollapsed)

  const toggleCollapsed = (key) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      saveCollapsed(next)
      return next
    })
  }

  useEffect(() => assetRegistry.subscribe(() => force(n => n + 1)), [])

  // Reserved dot-folders (e.g. the interop suite's '/.e2e' corral) are hidden
  // from the tree unless the debug escape hatch is on — see hiddenFolders.js.
  const revealHidden = showHiddenFolders()
  const folders = useMemo(
    () => assetRegistry.listFolders().filter(k => revealHidden || !isHiddenFolderKey(k)),
    [assetRegistry.listFolders().join('|'), revealHidden],
  )
  const tree = useMemo(() => buildTree(folders), [folders.join('|')])
  // Asset counts per folder key. Subtree counts (a folder shows the total
  // of its own assets plus everything underneath) match what artists
  // expect from a tree count badge.
  const counts = useMemo(() => {
    const direct = {}
    for (const a of assetRegistry.list()) {
      if (!revealHidden && isHiddenFolderSegments(a.path)) continue
      const k = (a.path ?? []).join('/')
      direct[k] = (direct[k] ?? 0) + 1
    }
    const subtree = {}
    for (const [k, n] of Object.entries(direct)) {
      const parts = k ? k.split('/') : []
      for (let i = 0; i <= parts.length; i++) {
        const prefix = parts.slice(0, i).join('/')
        subtree[prefix] = (subtree[prefix] ?? 0) + n
      }
    }
    return subtree
  }, [assetRegistry.list().length])

  // Close the context menu on any click / Esc.
  useEffect(() => {
    if (!menu) return
    const onDocClick = () => setMenu(null)
    const onKey = e => { if (e.key === 'Escape') setMenu(null) }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const handleNewFolder = (parentPath) => {
    setMenu(null)
    // Create a placeholder folder and immediately enter rename mode for it.
    const tentative = [...parentPath, 'new folder']
    assetRegistry.createFolder(tentative)
    setRenaming(tentative.join('/'))
  }

  return (
    <div data-testid="folder-tree" style={{
      width: 220, flexShrink: 0,
      background: C.bg, borderRight: `1px solid ${C.border}`,
      overflowY: 'auto', padding: '8px 6px',
      position: 'relative',
    }}>
      <div style={{
        ...head, padding: '0 6px 8px',
      }}>
        Folders
      </div>
      <FolderNode
        node={tree}
        depth={0}
        activeKey={activePath.join('/')}
        onSelectPath={onSelectPath}
        onDropAsset={onDropAsset}
        onDropFiles={onDropFiles}
        onOpenMenu={(x, y, path) => setMenu({ x, y, path })}
        onRootContextMenu={onRootContextMenu}
        renamingKey={renaming}
        onStartRename={key => setRenaming(key)}
        onCommitRename={(oldPath, newName) => {
          const verdict = validateFolderName(newName)
          if (!verdict.ok) {
            // Refuse loudly (never silently drop the keystroke) and leave the
            // placeholder/old name as it was — the rename editor closes so the
            // tree isn't stuck in edit mode behind the dialog.
            setRenaming(null)
            dialog.alert({ title: 'Reserved folder name', body: verdict.message, variant: 'error' })
            return
          }
          if (newName && newName !== oldPath[oldPath.length - 1]) {
            assetRegistry.renameFolder(oldPath, newName)
            const newKey = [...oldPath.slice(0, -1), newName].join('/')
            // Re-sync the active path if the rename touched it.
            if (activePath.join('/').startsWith(oldPath.join('/'))) {
              onSelectPath([...oldPath.slice(0, -1), newName, ...activePath.slice(oldPath.length)])
            }
            setRenaming(null)
          } else {
            setRenaming(null)
          }
        }}
        onCancelRename={() => setRenaming(null)}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        counts={counts}
      />
      {menu && (
        <NewAssetMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          extraItems={menu.path.length === 0 ? [] : [
            { key: '__rename',         label: 'Rename',        hint: 'F2' },
            { key: '__new-subfolder',  label: 'New subfolder' },
            { key: '__delete',         label: 'Delete',        danger: true },
          ]}
          onPick={(key) => {
            const targetPath = menu.path
            if (key === '__rename') {
              setRenaming(targetPath.join('/'))
            } else if (key === '__new-subfolder') {
              handleNewFolder(targetPath)
            } else if (key === '__delete') {
              dialog.confirm({
                title: 'Delete folder and contents?',
                body: `Delete folder "${targetPath[targetPath.length - 1]}" and everything inside it? This permanently deletes all assets in this folder and its subfolders (DB + storage). This cannot be undone.`,
                confirmLabel: 'Delete folder + assets',
                danger: true,
              }).then(ok => {
                if (!ok) return
                assetRegistry.deleteFolder(targetPath)
                if (activePath.join('/') === targetPath.join('/')) onSelectPath([])
              })
            } else {
              // Asset-creation key — bubble to ContentBrowser, scoped to
              // the folder the user right-clicked (not the active path).
              onCreateAtPath?.(key, targetPath)
            }
          }}
        />
      )}
    </div>
  )
}

// ─── Folder row ────────────────────────────────────────────────────────────

function FolderNode({ node, depth, activeKey, onSelectPath, onDropAsset, onDropFiles, onOpenMenu, onRootContextMenu, renamingKey, onStartRename, onCommitRename, onCancelRename, collapsed, onToggleCollapsed, counts }) {
  const [over, setOver] = useState(false)
  const key = node.path.join('/')
  const isActive = key === activeKey
  const isRoot = depth === 0
  const isRenaming = renamingKey === key
  // Root is always open. Collapsed set holds keys explicitly collapsed by
  // the user; everything else defaults to open.
  const open = isRoot ? true : !collapsed.has(key)
  // Stable hooks for tests: root row is 'folder-row-root'; nested rows join
  // their path segments with '--' (e.g. animations/body → folder-row-animations--body).
  const testKey = isRoot ? 'root' : node.path.join('--')

  const handleDrop = e => {
    e.preventDefault(); e.stopPropagation()
    setOver(false)
    // Asset-id wins over files: dragging an in-app asset card always means
    // "move this asset", even if the browser synthesized a file from an
    // inner <img> drag. Without this, the texture wizard kept opening when
    // the user tried to move a body-anim card because the avatar-snapshot
    // <img> bled a fake JPG into dataTransfer.files.
    const assetId = e.dataTransfer.getData('text/aos-asset-id')
    if (assetId) { onDropAsset?.(assetId, node.path); return }
    if (e.dataTransfer.files?.length) {
      onDropFiles?.(Array.from(e.dataTransfer.files), node.path)
    }
  }

  return (
    <div>
      <div
        data-testid={`folder-row-${testKey}`}
        onClick={() => onSelectPath(node.path)}
        onDoubleClick={e => {
          if (isRoot) return
          e.stopPropagation()
          onStartRename(key)
        }}
        onContextMenu={e => {
          e.preventDefault()
          // Both root (path=[]) and non-root folders open the same menu;
          // FolderTree decides whether to include Rename/Delete based
          // on path length.
          onOpenMenu(e.clientX, e.clientY, node.path)
        }}
        onDragOver={e => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={handleDrop}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '4px 6px',
          paddingLeft: 2 + depth * 12,
          // Active row = accent-soft wash + accent text (gass .stu-tree-row.active).
          color: isActive ? activeWash.color : C.text,
          background: over ? C.active : isActive ? activeWash.background : 'transparent',
          borderRadius: 'var(--m-radius-sm)',
          cursor: 'pointer',
          fontFamily: 'var(--m-font)', fontSize: 11,
          transition: 'background var(--m-ease), color var(--m-ease)',
        }}
      >
        {node.children.length > 0 ? (
          // 20×20 clickable caret (the old 12px-wide glyph was a sub-fingertip
          // target); the negative vertical margin keeps the dense row height.
          <span
            data-testid={`folder-caret-${testKey}`}
            onClick={e => { e.stopPropagation(); if (!isRoot) onToggleCollapsed(key) }}
            onMouseEnter={isRoot ? undefined : e => {
              e.currentTarget.style.background = C.hover
              e.currentTarget.style.color = C.text
            }}
            onMouseLeave={isRoot ? undefined : e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = C.dim
            }}
            style={{
              width: 20, height: 20, margin: '-3px 0', flex: 'none',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, borderRadius: 3,
              color: C.dim, cursor: isRoot ? 'default' : 'pointer',
            }}
          >{open ? '▾' : '▸'}</span>
        ) : (
          <span style={{ width: 20, flex: 'none' }} />
        )}
        {isRenaming ? (
          <RenameInput
            initial={node.name}
            onCommit={newName => onCommitRename(node.path, newName)}
            onCancel={onCancelRename}
          />
        ) : (
          <>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isRoot ? '/' : node.name}
            </span>
            {counts?.[key] > 0 && (
              <span style={{ color: C.dim, fontSize: 9.5, opacity: 0.6 }}>
                {counts[key]}
              </span>
            )}
          </>
        )}
      </div>
      {open && node.children.map(child => (
        <FolderNode
          key={child.path.join('/')}
          node={child}
          depth={depth + 1}
          activeKey={activeKey}
          onSelectPath={onSelectPath}
          onDropAsset={onDropAsset}
          onDropFiles={onDropFiles}
          onOpenMenu={onOpenMenu}
          onRootContextMenu={onRootContextMenu}
          renamingKey={renamingKey}
          onStartRename={onStartRename}
          onCommitRename={onCommitRename}
          onCancelRename={onCancelRename}
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          counts={counts}
        />
      ))}
    </div>
  )
}

// ─── Inline rename ──────────────────────────────────────────────────────────

function RenameInput({ initial, onCommit, onCancel }) {
  const ref = useRef(null)
  const [val, setVal] = useState(initial)
  // Enter and blur are two routes to the SAME commit, and since Enter's commit
  // is deferred (see below) they now overlap: the dialog that a refused commit
  // opens takes focus off this input, so onBlur fires and would commit a second
  // time — stacking a second identical alert behind the first. Commit once.
  const done = useRef(false)
  const commitOnce = name => {
    if (done.current) return
    done.current = true
    onCommit(name)
  }
  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])
  return (
    <input
      ref={ref}
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={() => commitOnce(val.trim())}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Enter') {
          // Commit on the NEXT tick, not synchronously via blur() here. A
          // reserved-name commit opens dialog.alert() (FolderTree's
          // onCommitRename), and DialogHost autofocuses that alert's OK
          // button the moment it mounts. Committing inline let that focus
          // shift land WHILE this same Enter keystroke was still being
          // dispatched (this keydown now, its keyup a beat later) — the
          // trailing keyup then activated the freshly-focused OK button and
          // dismissed the alert before it could ever be read, so a refusal
          // that's supposed to be LOUD flashed and vanished instead.
          // Deferring lets Enter finish dispatching before the dialog (and
          // its autofocus) exist at all.
          e.preventDefault()
          const name = val.trim()
          setTimeout(() => commitOnce(name), 0)
        }
        else if (e.key === 'Escape') { done.current = true; onCancel(); }
      }}
      onClick={e => e.stopPropagation()}
      style={{
        background: 'var(--m-panel-hi, #080a10)', border: `1px solid ${C.primary}`,
        color: C.text, fontFamily: 'var(--m-font)', fontSize: 11,
        padding: '1px 6px', borderRadius: 'var(--m-radius-sm)', outline: 'none',
        width: '100%', minWidth: 0,
      }}
    />
  )
}

// The previous ContextMenu / MenuItem / MenuDivider helpers were removed in
// favor of the shared NewAssetMenu, which handles both the asset-creation
// items and the folder-specific actions (Rename / New subfolder / Delete)
// via extraItems.
